import React, { useState, useEffect, useMemo } from 'react';
import { FilePlus2, Plus, Trash2, AlertTriangle } from 'lucide-react';
import { clientesService, puntovService } from '../../../services/api';
import Modal from './Modal';
import { fmtUsd } from './formato';

const LINEA_VACIA = { descripcion: '', cantidad: '1', precio_unitario: '' };
const claseInput = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none';

const centavos = (v) => Math.round(Number(v || 0) * 100);

// Cotización aparte de la mensualidad (instalación, integración, repuestos)
// para un cliente de Punto V. Sale con el mismo PDF puntov y no toca el
// cobro mensual ni el bloqueo de recarga.
// inicial: { cliente_id, empresa, contacto } para abrirla ya cargada desde
// un cliente de Punto V.
const FormularioCotizacionLibre = ({ onClose, onCreada, inicial = {} }) => {
  const [clientes, setClientes] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [form, setForm] = useState({
    cliente_id: inicial.cliente_id ? String(inicial.cliente_id) : '',
    empresa: inicial.empresa || '',
    concepto: '',
    contacto: inicial.contacto || '',
    correo: '',
    enviar: true
  });
  const [lineas, setLineas] = useState([{ ...LINEA_VACIA }]);
  // Una llave por formulario abierto: un doble clic no crea dos cotizaciones, pero otra identica a proposito si.
  const [llave] = useState(() => window.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    clientesService.getAll({ activo: true })
      .then(res => {
        const activos = (res.data || []).filter(c => c.activo !== false);
        setClientes(activos);
        const preelegido = activos.find(c => String(c.id) === String(inicial.cliente_id));
        if (preelegido?.email) setForm(prev => (prev.correo ? prev : { ...prev, correo: preelegido.email }));
      })
      .catch(() => setError('No se pudieron cargar los clientes del ERP'));
    puntovService.listarClientes()
      .then(res => setEmpresas((res.data || []).filter(c => !c.archivado)))
      .catch(() => {});
  }, [inicial.cliente_id]);

  const cambiar = (campo, valor) => setForm(prev => ({ ...prev, [campo]: valor }));

  const elegirCliente = (id) => {
    const cliente = clientes.find(c => String(c.id) === id);
    setForm(prev => ({ ...prev, cliente_id: id, correo: cliente?.email || prev.correo }));
  };

  const cambiarLinea = (i, campo, valor) => setLineas(prev => prev.map((l, j) => (j === i ? { ...l, [campo]: valor } : l)));
  const quitarLinea = (i) => setLineas(prev => (prev.length === 1 ? prev : prev.filter((_, j) => j !== i)));

  // Solo para mostrar mientras se escribe; el total que vale es el que
  // guarda la base al crear la cotización.
  const totalVista = useMemo(
    () => lineas.reduce((acc, l) => acc + Math.round(Number(l.cantidad || 0) * centavos(l.precio_unitario)), 0) / 100,
    [lineas]
  );

  const guardar = async (e) => {
    e.preventDefault();
    setError('');
    setGuardando(true);
    try {
      const res = await puntovService.crearCotizacionLibre({
        cliente_id: Number(form.cliente_id),
        empresa: form.empresa || null,
        concepto: form.concepto.trim(),
        contacto: form.contacto.trim() || null,
        correo: form.correo.trim() || null,
        enviar: form.enviar,
        idempotency_key: llave,
        lineas: lineas.map(l => ({
          descripcion: l.descripcion.trim(),
          cantidad: Number(l.cantidad),
          precio_unitario: String(l.precio_unitario).trim()
        }))
      });
      onCreada(res.data);
    } catch (err) {
      setError(err.message || 'No se pudo crear la cotización');
      setGuardando(false);
    }
  };

  return (
    <Modal
      titulo="Nueva cotización Punto V"
      subtitulo="Aparte de la mensualidad: no cambia el cobro ni el bloqueo de recarga"
      icono={FilePlus2}
      onClose={onClose}
      bloqueado={guardando}
      ancho="xl"
    >
      <form id="form-cotizacion-libre" onSubmit={guardar} className="space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="libre-cliente" className="block text-xs font-medium text-gray-700 mb-1">Cliente *</label>
            <select
              id="libre-cliente"
              value={form.cliente_id}
              onChange={(e) => elegirCliente(e.target.value)}
              required
              className={claseInput}
            >
              <option value="">Seleccione…</option>
              {clientes.map(c => (
                <option key={c.id} value={c.id}>
                  {[c.nombre, c.apellido].filter(Boolean).join(' ')}{c.rif ? ` · ${c.rif}` : ''}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="libre-empresa" className="block text-xs font-medium text-gray-700 mb-1">
              Cliente en Punto V <span className="font-normal text-gray-400">(opcional)</span>
            </label>
            <select
              id="libre-empresa"
              value={form.empresa}
              onChange={(e) => cambiar('empresa', e.target.value)}
              className={claseInput}
            >
              <option value="">Sin vincular</option>
              {empresas.map(e => <option key={e.base_datos} value={e.base_datos}>{e.slug}</option>)}
            </select>
          </div>
          <div className="md:col-span-2">
            <label htmlFor="libre-concepto" className="block text-xs font-medium text-gray-700 mb-1">Concepto *</label>
            <input
              id="libre-concepto"
              value={form.concepto}
              onChange={(e) => cambiar('concepto', e.target.value)}
              required
              placeholder="Instalación de máquina nueva"
              className={claseInput}
            />
          </div>
          <div>
            <label htmlFor="libre-contacto" className="block text-xs font-medium text-gray-700 mb-1">Contacto</label>
            <input
              id="libre-contacto"
              value={form.contacto}
              onChange={(e) => cambiar('contacto', e.target.value)}
              placeholder="Juan Elizondo"
              className={claseInput}
            />
          </div>
          <div>
            <label htmlFor="libre-correo" className="block text-xs font-medium text-gray-700 mb-1">Enviar a</label>
            <input
              id="libre-correo"
              type="email"
              value={form.correo}
              onChange={(e) => cambiar('correo', e.target.value)}
              placeholder="Correo del cliente"
              className={claseInput}
            />
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-semibold text-gray-800">Líneas</h4>
            <button
              type="button"
              onClick={() => setLineas(prev => [...prev, { ...LINEA_VACIA }])}
              className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
            >
              <Plus size={15} /> Agregar línea
            </button>
          </div>
          <div className="border border-gray-200 rounded-xl overflow-hidden">
            <div className="hidden md:grid grid-cols-[1fr_6rem_8rem_7rem_2.5rem] gap-2 px-3 py-2 bg-gray-50 text-xs font-medium text-gray-500">
              <span>Descripción</span><span>Cantidad</span><span>Precio (USD)</span><span className="text-right">Importe</span><span />
            </div>
            {lineas.map((l, i) => (
              <div key={i} className="grid grid-cols-2 md:grid-cols-[1fr_6rem_8rem_7rem_2.5rem] gap-2 px-3 py-2 border-t border-gray-100 first:border-t-0 md:first:border-t items-center">
                <input
                  aria-label={`Descripción línea ${i + 1}`}
                  value={l.descripcion}
                  onChange={(e) => cambiarLinea(i, 'descripcion', e.target.value)}
                  required
                  placeholder="Descripción"
                  className={`${claseInput} col-span-2 md:col-span-1`}
                />
                <input
                  aria-label={`Cantidad línea ${i + 1}`}
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={l.cantidad}
                  onChange={(e) => cambiarLinea(i, 'cantidad', e.target.value)}
                  required
                  className={`${claseInput} tabular-nums`}
                />
                <input
                  aria-label={`Precio línea ${i + 1}`}
                  type="number"
                  min="0"
                  step="0.01"
                  value={l.precio_unitario}
                  onChange={(e) => cambiarLinea(i, 'precio_unitario', e.target.value)}
                  required
                  placeholder="0.00"
                  className={`${claseInput} tabular-nums`}
                />
                <span className="text-sm text-right tabular-nums text-gray-700">
                  {fmtUsd(Math.round(Number(l.cantidad || 0) * centavos(l.precio_unitario)) / 100)}
                </span>
                <button
                  type="button"
                  onClick={() => quitarLinea(i)}
                  disabled={lineas.length === 1}
                  aria-label={`Quitar línea ${i + 1}`}
                  className="p-1.5 text-gray-400 hover:text-red-600 disabled:opacity-30 justify-self-end"
                >
                  <Trash2 size={15} />
                </button>
              </div>
            ))}
          </div>
          <div className="flex justify-end items-baseline gap-3 mt-3">
            <span className="text-sm text-gray-500">Total sin IVA</span>
            <span className="text-lg font-bold tabular-nums text-gray-900">{fmtUsd(totalVista)}</span>
          </div>
        </div>

        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" checked={form.enviar} onChange={(e) => cambiar('enviar', e.target.checked)} />
          Enviar por correo al crearla
        </label>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm flex items-start gap-2">
            <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" /> {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} disabled={guardando} className="px-4 py-2 text-sm text-gray-700 rounded-lg hover:bg-gray-100">
            Cancelar
          </button>
          <button
            type="submit"
            disabled={guardando}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50 flex items-center gap-2"
          >
            <FilePlus2 size={16} />
            {guardando ? 'Creando…' : form.enviar ? 'Crear y enviar' : 'Crear cotización'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default FormularioCotizacionLibre;
